import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { NumberedPagination } from '@kioskgaming/ui';
import {
  Plus,
  Shield,
  Trash2,
  Lock,
  Unlock,
  UserPlus,
  X,
  Eye,
  EyeOff,
  RefreshCw,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/Card';
import Button from '../../ui/Button';
import Input from '../../ui/Input';
import { LoadingSpinner } from '../../ui/Loading';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/Table';
import {
  useAdmins,
  useCreateAdmin,
  useDeleteAdmin,
  useResetAdminPassword,
  useUpdateAdmin,
  useSetPermissions,
  useGameProviders,
  useAdminPermissions,
} from '../../../hooks/useApi';
import {
  AdminAccount,
  AdminListFilters,
  CreateAdminRequest,
  UpdateAdminRequest,
  AdminRole,
  GameProvider,
} from '../../../types';
import { useAuth, usePermission } from '../../../hooks/useAuth';
import {
  ADMIN_ROLE_OPTIONS,
  ROLE_PERMISSIONS,
  getAdminRoleLabel,
  toBackendPermissionCodes,
} from '../../../config/permissions';
import AdminUpdateUsernameModal from '../../shared/AdminUpdateUsernameModal';
import { isValidEmail } from '../../../utils';
import ManagePermissionsModal from './ManagePermissionsModal';
import RolePermissionSummary from './RolePermissionSummary';
import { PageHeader } from '@kioskgaming/ui';

/** Roles available when creating a new admin (system_admin cannot be assigned via UI) */
const CREATE_ROLE_OPTIONS = ADMIN_ROLE_OPTIONS.filter((r) => r.value !== 'system_admin');

/** Admin password rules: min 8 chars, 1 uppercase, 1 lowercase, 1 number, 1 special (@$!%?&) */
const ADMIN_PASSWORD_MIN_LENGTH = 8;
const ADMIN_PASSWORD_SPECIAL = /[@$!%?&]/;
const ADMIN_PASSWORD_RULES =
  'Password must be at least 8 characters with at least 1 uppercase, 1 lowercase, 1 number and 1 special character (@$!%?&).';

function validateAdminPassword(password: string): boolean {
  if (!password || password.length < ADMIN_PASSWORD_MIN_LENGTH) return false;
  if (!/[A-Z]/.test(password)) return false;
  if (!/[a-z]/.test(password)) return false;
  if (!/[0-9]/.test(password)) return false;
  if (!ADMIN_PASSWORD_SPECIAL.test(password)) return false;
  return true;
}

const AdminManagement: React.FC = () => {
  const { user: currentAdmin } = useAuth();
  // KIOSK-720: only system_admin may create/edit/delete admins (backend enforces via requireSystemAdmin)
  const canManageAdmins = currentAdmin?.role === 'system_admin';
  /** Only system_admin can reset another admin's password (API enforces this) */
  const canResetPassword = currentAdmin?.role === 'system_admin';

  const [filters, setFilters] = useState<AdminListFilters>({
    page: 1,
    limit: 10,
    role: undefined,
    active: undefined,
    search: '',
  });

  const [searchTerm, setSearchTerm] = useState('');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState<AdminAccount | null>(null);
  const [permissionsModalAdmin, setPermissionsModalAdmin] = useState<AdminAccount | null>(null);
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);

  const [createForm, setCreateForm] = useState<CreateAdminRequest>({
    email: '',
    username: '',
    name: '',
    password: '',
    role: 'platform_admin',
  });
  const [selectedCreateGameProviders, setSelectedCreateGameProviders] = useState<string[]>([]);
  const [createErrorMessage, setCreateErrorMessage] = useState<string>('');

  const [manageForm, setManageForm] = useState<UpdateAdminRequest>({
    role: undefined,
    is_active: undefined,
  });
  const [resetPassword, setResetPassword] = useState('');
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [showCreatePassword, setShowCreatePassword] = useState(false);
  const [usernameModalOpen, setUsernameModalOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const { data: adminsData, isLoading, error, isFetching } = useAdmins(filters);
  const { data: gameProvidersData } = useGameProviders();
  // managedGameProviders lives on the AdminPermission join row, not on the admin list object —
  // fetch it separately so the Manage modal's permission summary shows the real scoped providers.
  const { data: selectedAdminPermissionsData } = useAdminPermissions(selectedAdmin?.id || '');
  const createAdmin = useCreateAdmin();
  const updateAdmin = useUpdateAdmin();
  const deleteAdmin = useDeleteAdmin();
  const resetAdminPassword = useResetAdminPassword();
  const setPermissions = useSetPermissions();

  const totalAdmins = adminsData?.data?.total ?? 0;
  const admins = adminsData?.data?.items ?? [];
  const totalPages = useMemo(() => {
    if (!adminsData?.data) return 1;
    const { total, limit } = adminsData.data;
    if (!limit) return 1;
    return Math.max(1, Math.ceil(total / limit));
  }, [adminsData]);

  const gameProviders = useMemo(() => {
    return gameProvidersData?.data?.items || [];
  }, [gameProvidersData]);

  const selectedAdminManagedGameProviders = useMemo(() => {
    const permissions = selectedAdminPermissionsData?.data?.permissions || [];
    const codes = new Set<string>();
    permissions.forEach((p) => {
      if (Array.isArray(p.managedGameProviders)) {
        p.managedGameProviders.forEach((code) => codes.add(code));
      }
    });
    return Array.from(codes);
  }, [selectedAdminPermissionsData]);

  // Only finance_admin and operation_admin require game provider scoping.
  // system_admin/super_admin have full access; platform/support don't perform wallet ops.
  const hasCreateGameProviderRestriction = useMemo(() => {
    return createForm.role === 'finance_admin' || createForm.role === 'operation_admin';
  }, [createForm.role]);

  const requiresCreateGameProviderSelection = useMemo(() => {
    return createForm.role === 'finance_admin' || createForm.role === 'operation_admin';
  }, [createForm.role]);

  useEffect(() => {
    if (!selectedAdmin) return;
    const latest = admins.find(a => a.id === selectedAdmin.id);
    const adminToUse = latest || selectedAdmin;
    setSelectedAdmin(adminToUse);
    setManageForm({
      role: adminToUse.role,
      is_active: adminToUse.is_active,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [admins, selectedAdmin?.id]);

  const handleSearch = (event: React.FormEvent) => {
    event.preventDefault();
    setFilters(prev => ({
      ...prev,
      search: searchTerm.trim(),
      page: 1,
    }));
  };

  const handleRoleFilter = (role: string) => {
    setFilters(prev => ({
      ...prev,
      role: role || undefined,
      page: 1,
    }));
  };

  const handleActiveFilter = (value: string) => {
    setFilters(prev => ({
      ...prev,
      active: value === '' ? undefined : value === 'true',
      page: 1,
    }));
  };

  const handlePageChange = (page: number) => {
    setFilters(prev => ({
      ...prev,
      page,
    }));
  };

  const handleCreateAdmin = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!canManageAdmins) return;
    if (!isValidEmail(createForm.email.trim())) {
      toast.error('Please enter a valid email address — it is required to receive the login OTP.');
      return;
    }
    if (!validateAdminPassword(createForm.password)) {
      toast.error(ADMIN_PASSWORD_RULES);
      return;
    }

    // Permissions are fixed by role — derive directly from ROLE_PERMISSIONS.
    // Strip frontend-only codes (dashboard, manualGameDepositManagement, …) that the backend
    // catalog does not recognize, otherwise setPermissions rejects the whole call.
    const rolePermissionCodes = toBackendPermissionCodes(ROLE_PERMISSIONS[createForm.role] ?? []);

    // Validate: roles with walletManagement or gameProviderManagement require at least one game provider
    if (requiresCreateGameProviderSelection && selectedCreateGameProviders.length === 0) {
      setCreateErrorMessage('Please select at least one game provider for this role.');
      return;
    }

    setCreateErrorMessage('');

    try {
      const response = await createAdmin.mutateAsync(createForm);
      const newAdminId = response?.data?.admin?.id;

      // Always set permissions based on role
      if (newAdminId && rolePermissionCodes.length > 0) {
        try {
          const requestData: { permissionCodes: string[]; managedGameProviders?: string[] } = {
            permissionCodes: rolePermissionCodes,
          };

          if (hasCreateGameProviderRestriction) {
            requestData.managedGameProviders = selectedCreateGameProviders;
          }

          await setPermissions.mutateAsync({
            adminId: newAdminId,
            data: requestData,
          });
        } catch (permError) {
          console.error('Failed to set permissions:', permError);
          setCreateErrorMessage('Failed to set permissions. Please try again.');
          return;
        }
      }

      setCreateForm({ email: '', username: '', name: '', password: '', role: 'platform_admin' });
      setSelectedCreateGameProviders([]);
      setCreateErrorMessage('');
      setIsCreateOpen(false);
    } catch (err) {
      console.error('Create admin error:', err);
      setCreateErrorMessage('Failed to create admin. Please try again.');
    }
  };

  const handleUpdateAdmin = async () => {
    if (!selectedAdmin || !canManageAdmins) return;
    try {
      await updateAdmin.mutateAsync({
        adminId: selectedAdmin.id,
        data: {
          role: manageForm.role,
          is_active: manageForm.is_active,
        },
      });
    } catch (err) {
      console.error('Update admin error:', err);
    }
  };

  const generateRandomPassword = () => {
    const length = 12;
    const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789@$!%?&';
    let password = '';
    for (let i = 0; i < length; i++) {
      password += charset.charAt(Math.floor(Math.random() * charset.length));
    }
    setResetPassword(password);
  };

  const handleResetPassword = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!selectedAdmin || !canManageAdmins) return;
    if (!validateAdminPassword(resetPassword)) {
      toast.error(ADMIN_PASSWORD_RULES);
      return;
    }
    try {
      await resetAdminPassword.mutateAsync({
        adminId: selectedAdmin.id,
        data: { password: resetPassword },
      });
      setResetPassword('');
      setShowResetPassword(false);
    } catch (err) {
      console.error('Reset admin password error:', err);
    }
  };

  const handleDeleteAdmin = async () => {
    if (!selectedAdmin || !canManageAdmins) return;
    try {
      await deleteAdmin.mutateAsync({ adminId: selectedAdmin.id });
      setSelectedAdmin(null);
      setConfirmDelete(false);
    } catch (err) {
      console.error('Delete admin error:', err);
    }
  };

  const isSelf = (admin: AdminAccount | null) => {
    if (!admin || !currentAdmin) return false;
    return admin.id === currentAdmin.id;
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Admin Management"
        description="Manage admin accounts and access permissions"
        actions={
          canManageAdmins ? (
            <Button onClick={() => setIsCreateOpen(prev => !prev)}>
              <Plus className="h-4 w-4 mr-2" />
              Create Admin
            </Button>
          ) : null
        }
      />

      <Card>
        <CardContent className="p-6 space-y-4">
          <form onSubmit={handleSearch} className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1">
              <Input
                placeholder="Search by email or name"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <select
                className="border rounded-md px-3 py-2 text-sm"
                value={filters.role || ''}
                onChange={(event) => handleRoleFilter(event.target.value)}
              >
                <option value="">All roles</option>
                {ADMIN_ROLE_OPTIONS.map(role => (
                  <option key={role.value} value={role.value}>{role.label}</option>
                ))}
              </select>

              <select
                className="border rounded-md px-3 py-2 text-sm"
                value={
                  typeof filters.active === 'boolean'
                    ? String(filters.active)
                    : ''
                }
                onChange={(event) => handleActiveFilter(event.target.value)}
              >
                <option value="">All statuses</option>
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>

              <Button type="submit" variant="outline">
                Search
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Create Admin Modal */}
      {canManageAdmins && isCreateOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 !mt-0"
          onClick={(e) => {
            if (e.target === e.currentTarget && !createAdmin.isLoading) {
              setIsCreateOpen(false);
              setSelectedCreateGameProviders([]);
            }
          }}
        >
          <Card
            className="w-full max-w-4xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
              <CardTitle className="text-lg flex items-center gap-2">
                <UserPlus className="h-5 w-5 text-primary" />
                Create new admin
              </CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setIsCreateOpen(false);
                  setSelectedCreateGameProviders([]);
                  setCreateErrorMessage('');
                }}
                disabled={createAdmin.isLoading}
              >
                <X className="h-4 w-4" />
              </Button>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleCreateAdmin} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <Input
                    required
                    type="email"
                    placeholder="admin@company.com"
                    value={createForm.email}
                    onChange={(event) => setCreateForm(prev => ({ ...prev, email: event.target.value.trim() }))}
                  />
                  <p className="text-xs text-muted-foreground">
                    Must be entered accurately — the login OTP is sent to this email.
                  </p>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Username</label>
                  <Input
                    required
                    type="text"
                    autoComplete="username"
                    placeholder="Unique login username"
                    value={createForm.username}
                    onChange={(event) => setCreateForm(prev => ({ ...prev, username: event.target.value.trim() }))}
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Display name</label>
                  <Input
                    required
                    value={createForm.name}
                    onChange={(event) => setCreateForm(prev => ({ ...prev, name: event.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Password</label>
                  <div className="relative">
                    <Input
                      required
                      type={showCreatePassword ? 'text' : 'password'}
                      value={createForm.password}
                      onChange={(event) => setCreateForm(prev => ({ ...prev, password: event.target.value }))}
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowCreatePassword(!showCreatePassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showCreatePassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Min 8 characters, at least 1 uppercase, 1 lowercase, 1 number and 1 special character (@$!%?&).
                  </p>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium" htmlFor="create-role">Role</label>
                  <select
                    id="create-role"
                    className="border rounded-md px-3 py-2 text-sm w-full"
                    value={createForm.role}
                    onChange={(event) => {
                      const newRole = event.target.value as AdminRole;
                      setCreateForm(prev => ({ ...prev, role: newRole }));
                      setSelectedCreateGameProviders([]);
                      setCreateErrorMessage('');
                    }}
                  >
                    {CREATE_ROLE_OPTIONS.map(role => (
                      <option key={role.value} value={role.value}>{role.label}</option>
                    ))}
                  </select>
                </div>

                {/* Permissions and Game Providers - hidden for system_admin and super_admin (have all permissions) */}
                {createForm.role !== 'system_admin' && createForm.role !== 'super_admin' && (
                  <>
                {/* Permissions are fixed by role — read-only summary with proper names */}
                <RolePermissionSummary
                  role={createForm.role}
                  showGameProviders={false}
                  className="md:col-span-2"
                />

                {/* Game Provider Selection - Show when transactionManagement, walletManagement, or gameProviderManagement is selected */}
                {hasCreateGameProviderRestriction && (
                  <div className="md:col-span-2 space-y-2">
                    <label className="text-sm font-medium">Managed Game Providers</label>
                    <p className="text-xs text-muted-foreground">
                      {requiresCreateGameProviderSelection
                        ? 'Select which game providers this admin can operate on (wallet adjustment, transactions). At least one provider is required.'
                        : 'Select which game providers this admin can manage. Leave empty to allow access to all providers.'}
                    </p>
                    {createErrorMessage && (
                      <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
                        <p className="text-sm text-destructive">{createErrorMessage}</p>
                      </div>
                    )}
                    <div className="border rounded-md p-4 max-h-64 overflow-y-auto bg-gray-50">
                      {gameProviders.length === 0 ? (
                        <p className="text-sm text-muted-foreground">Loading game providers...</p>
                      ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {gameProviders.map((provider: GameProvider) => {
                            const isSelected = selectedCreateGameProviders.includes(provider.code);
                            return (
                              <label
                                key={provider.id}
                                className="flex items-start space-x-2 p-2 rounded cursor-pointer hover:bg-white transition-colors"
                              >
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setSelectedCreateGameProviders(prev => {
                                        const newProviders = [...prev, provider.code];
                                        // Clear error message when selecting game providers
                                        if (requiresCreateGameProviderSelection && newProviders.length > 0) {
                                          setCreateErrorMessage('');
                                        }
                                        return newProviders;
                                      });
                                    } else {
                                      setSelectedCreateGameProviders(prev => prev.filter(p => p !== provider.code));
                                    }
                                  }}
                                  className="mt-1"
                                />
                                <div className="flex-1">
                                  <div className="text-sm font-medium">{provider.name}</div>
                                  <div className="text-xs text-muted-foreground">
                                    <code>{provider.code}</code> • {provider.currency} • Status: {provider.status}
                                  </div>
                                </div>
                              </label>
                            );
                          })}
                        </div>
                      )}
                    </div>
                    {selectedCreateGameProviders.length > 0 && (
                      <p className="text-xs text-muted-foreground">
                        {selectedCreateGameProviders.length} game provider(s) selected
                      </p>
                    )}
                  </div>
                )}
                  </>
                )}

                <div className="md:col-span-2 flex gap-2 justify-end pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setIsCreateOpen(false);
                      setSelectedCreateGameProviders([]);
                      setCreateErrorMessage('');
                    }}
                    disabled={createAdmin.isLoading}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={createAdmin.isLoading || !canManageAdmins}
                  >
                    {createAdmin.isLoading ? 'Creating...' : 'Create admin'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center h-48">
              <LoadingSpinner />
            </div>
          ) : error ? (
            <div className="p-6 text-center text-red-600">
              Failed to load admin list.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Email</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="w-24 text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {admins.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-10 text-muted-foreground">
                        No admins found
                      </TableCell>
                    </TableRow>
                  ) : (
                    admins.map((admin) => (
                      <TableRow key={admin.id}>
                        <TableCell className="font-medium">{admin.email}</TableCell>
                        <TableCell>{admin.name || '-'}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Shield className="h-4 w-4 text-primary flex-shrink-0" />
                            <span>{getAdminRoleLabel(admin.role)}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          {admin.is_active ? (
                            <span className="inline-flex items-center px-2 py-1 rounded-full bg-green-100 text-green-700 text-xs font-medium">
                              Active
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-1 rounded-full bg-red-100 text-red-700 text-xs font-medium">
                              Inactive
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            {canManageAdmins && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setPermissionsModalAdmin(admin);
                                  setShowPermissionsModal(true);
                                }}
                              >
                                <Shield className="h-4 w-4 mr-2" />
                                Permissions
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setSelectedAdmin(admin)}
                            >
                              Manage
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
        <div className="flex items-center justify-between px-6 py-4 text-sm text-muted-foreground">
          <div>
            Total: <span className="font-medium text-foreground">{totalAdmins}</span>
            {isFetching && !isLoading && <span className="ml-2">(Refreshing...)</span>}
          </div>
          <NumberedPagination
            currentPage={filters.page || 1}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            variant="admin"
            prevLabel="Prev"
            nextLabel="Next"
          />
        </div>
      </Card>

      {/* Manage Admin Modal */}
      {selectedAdmin && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 !mt-0"
          onClick={(e) => {
            if (e.target === e.currentTarget && !updateAdmin.isLoading && !resetAdminPassword.isLoading && !deleteAdmin.isLoading) {
              setSelectedAdmin(null);
              setConfirmDelete(false);
              setResetPassword('');
              setShowResetPassword(false);
            }
          }}
        >
          <Card
            className="w-full max-w-3xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
              <div>
                <CardTitle className="text-lg">Manage: {selectedAdmin.email}</CardTitle>
                <p className="text-sm text-muted-foreground mt-1">ID: {selectedAdmin.id}</p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSelectedAdmin(null);
                  setConfirmDelete(false);
                  setResetPassword('');
                  setShowResetPassword(false);
                }}
                disabled={updateAdmin.isLoading || resetAdminPassword.isLoading || deleteAdmin.isLoading}
              >
                <X className="h-4 w-4" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Role</label>
                  <select
                    className="border rounded-md px-3 py-2 text-sm w-full"
                    value={manageForm.role || selectedAdmin.role}
                    onChange={(event) => setManageForm(prev => ({
                      ...prev,
                      role: event.target.value as AdminRole,
                    }))}
                    disabled={isSelf(selectedAdmin)}
                  >
                    {ADMIN_ROLE_OPTIONS.map(role => (
                      <option key={role.value} value={role.value}>{role.label}</option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Status</label>
                  <Button
                    type="button"
                    variant={manageForm.is_active ?? selectedAdmin.is_active ? 'outline' : 'destructive'}
                    onClick={() => setManageForm(prev => ({
                      ...prev,
                      is_active: !(prev.is_active ?? selectedAdmin.is_active),
                    }))}
                    disabled={isSelf(selectedAdmin)}
                    className="w-full"
                  >
                    {manageForm.is_active ?? selectedAdmin.is_active ? (
                      <>
                        <Unlock className="h-4 w-4 mr-2" /> Active
                      </>
                    ) : (
                      <>
                        <Lock className="h-4 w-4 mr-2" /> Inactive
                      </>
                    )}
                  </Button>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium invisible">Update</label>
                  <Button
                    variant="primary"
                    onClick={handleUpdateAdmin}
                    disabled={updateAdmin.isLoading || !canManageAdmins}
                    className="w-full"
                  >
                    {updateAdmin.isLoading ? 'Updating...' : 'Save changes'}
                  </Button>
                </div>
              </div>

              {/* Show the permissions the selected role grants — updates live when role changes */}
              <RolePermissionSummary
                role={manageForm.role || selectedAdmin.role}
                managedGameProviders={selectedAdminManagedGameProviders}
                gameProviders={gameProviders}
              />

              {canResetPassword && (
                <div className="space-y-4">
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <div className="min-w-0">
                      <p className="text-sm font-medium">Username</p>
                      <p className="text-xs text-muted-foreground break-all">{selectedAdmin.username || '—'}</p>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="w-full sm:w-auto shrink-0"
                      onClick={() => setUsernameModalOpen(true)}
                    >
                      Change username
                    </Button>
                  </div>
                <form onSubmit={handleResetPassword} className="space-y-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Reset password</label>
                    <div className="flex flex-col sm:flex-row gap-2">
                      <div className="relative flex-1 min-w-0">
                        <Input
                          required
                          type={showResetPassword ? 'text' : 'password'}
                          value={resetPassword}
                          onChange={(event) => setResetPassword(event.target.value)}
                          className="pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowResetPassword(!showResetPassword)}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        >
                          {showResetPassword ? (
                            <EyeOff className="h-4 w-4" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={generateRandomPassword}
                        className="w-full sm:w-auto flex-shrink-0"
                        title="Generate random password"
                      >
                        <RefreshCw className="h-4 w-4 mr-2" />
                        Random
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Min 8 characters, at least 1 uppercase, 1 lowercase, 1 number and 1 special character (@$!%?&).
                    </p>
                  </div>
                  <div className="flex justify-end">
                    <Button
                      type="submit"
                      disabled={resetAdminPassword.isLoading}
                    >
                      {resetAdminPassword.isLoading ? 'Resetting...' : 'Reset password'}
                    </Button>
                  </div>
                </form>
                </div>
              )}

              {!isSelf(selectedAdmin) && canManageAdmins && (
                <div className="flex items-center justify-between p-4 border rounded-md bg-red-50">
                  <div>
                    <p className="text-sm font-semibold text-red-700">Delete admin</p>
                    <p className="text-xs text-red-500">This action cannot be undone.</p>
                  </div>
                  <div className="flex gap-2">
                    {confirmDelete ? (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setConfirmDelete(false)}
                        >
                          Cancel
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={handleDeleteAdmin}
                          disabled={deleteAdmin.isLoading}
                        >
                          {deleteAdmin.isLoading ? 'Deleting...' : 'Delete now'}
                        </Button>
                      </>
                    ) : (
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => setConfirmDelete(true)}
                      >
                        <Trash2 className="h-4 w-4 mr-2" /> Delete admin
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {canResetPassword && selectedAdmin ? (
        <AdminUpdateUsernameModal
          open={usernameModalOpen}
          onClose={() => setUsernameModalOpen(false)}
          target={{ kind: 'admin', adminId: selectedAdmin.id }}
          currentUsername={selectedAdmin.username}
          title="Change admin username"
          onSuccess={() => {
            toast.success('Admin username updated. Sessions were signed out.');
            setUsernameModalOpen(false);
            window.location.reload();
          }}
        />
      ) : null}

      {/* Permissions Management Modal */}
      <ManagePermissionsModal
        admin={permissionsModalAdmin}
        isOpen={showPermissionsModal}
        onClose={() => {
          setShowPermissionsModal(false);
          setPermissionsModalAdmin(null);
        }}
        onSuccess={() => {
          // Refresh admin list
          if (selectedAdmin?.id === permissionsModalAdmin?.id) {
            // Refresh selected admin if viewing their details
            const adminToRefresh = admins.find(a => a.id === permissionsModalAdmin?.id);
            if (adminToRefresh) {
              setSelectedAdmin(adminToRefresh);
            }
          }
        }}
      />
    </div>
  );
};

export default AdminManagement;


